// All tuning knobs for client-side face normalization + the processing mesh
// overlay live here. Geometry constants are shared by the canvas draw and the
// overlay landmark mapping so the mesh stays registered with the pixels.

// ---- Normalized output frame (3:4, matches backend ASPECT_W/H) ----
export const OUT_W = 1080;
export const OUT_H = 1440;

// Eye midpoint target. y=0.40H keeps the eyes inside the backend's
// fixed-coordinate censor-bar band (y 0.28H–0.44H) while leaving the head
// room to span the full 3:4 frame (crown near top, chin near bottom).
export const EYE_MID_X_FRAC = 0.5;
export const EYE_MID_Y_FRAC = 0.4;

// Interocular distance as a fraction of output width. 0.34 makes the head
// span ~90% of the 3:4 frame height — the face FILLS the frame; no
// head-and-shoulders dead space.
export const TARGET_IOD_FRAC = 0.34;

// Clamp upscaling of tiny/distant faces — beyond this the crop turns to mush;
// the face stays centered/derolled, just smaller than target.
export const MAX_UPSCALE = 4;

// The output frame must be fully covered by source pixels — no black filler
// borders in the stored image. When a face sits near a source edge, the scale
// is pushed ABOVE target (tighter crop) until the frame is covered, up to
// this hard cap (beyond it, sharpness loss outweighs the border).
export const COVER_SCALE_MAX = 6;

export const JPEG_QUALITY = 0.9;

// ---- Model assets (vendored under public/models/, see its README) ----
export const MODEL_ASSET_PATH = '/models/face_landmarker.task';
export const WASM_BASE_PATH = '/models/wasm';
// The tasks-vision JS itself is loaded at runtime from this vendored ESM
// bundle (not webpack-bundled): its internal dynamic requires trip webpack's
// "critical dependency" warning, which fails CI builds.
export const VISION_BUNDLE_PATH = '/models/wasm/vision_bundle.mjs';
export const MODEL_LOAD_TIMEOUT_MS = 4000;

// ---- MediaPipe FaceMesh canonical landmark indices ----
export const LEFT_EYE_CORNERS = [33, 133];
export const RIGHT_EYE_CORNERS = [362, 263];

// ---- Mesh overlay tuning (Aurum, DESIGN.md v1.2 §6.1 + §16.2) ----
export const MESH_EDGE_STRIDE = 3; // every 3rd tessellation edge (~850 of ~2556)
export const MESH_NODE_COLOR = '#C9A45C'; // Aurum — sanctioned face-geometry motif
export const MESH_NODE_BRIGHT = '#E6C083'; // Aurum Bright — scan-line core
export const MESH_SCAN_CORE = '#FFF3DC'; // scan-line inner highlight
export const MESH_EDGE_ALPHA = 0.3; // decorative-mode lines ("1px Aurum @ 30%", §6.1)
export const MESH_NODE_RADIUS_FRAC = 0.004; // of canvas width
export const MESH_PULSE_PERIOD_MS = 4000; // base wander period (mesh-pulse register)
export const MESH_DPR_CAP = 2;

// Entry reveal: Aurum scan-line sweeps top→bottom once, igniting the mesh.
// 1200ms matches the §6.1 hero "dots fade in over 1200ms staggered" register.
export const REVEAL_DURATION_MS = 1200;
export const REVEAL_IGNITE_MS = 200; // per-node alpha ramp after the line passes

// Roaming illumination: invisible "shimmer lights" wander the face on
// deterministic Lissajous paths; mesh points AND edges near a light are lit,
// the rest of the mesh sits dark. Highlight falls off as a gaussian of
// distance (sigma = SHIMMER_RADIUS, in output-frame fractions).
export const SHIMMER_CENTERS = 2;
export const SHIMMER_RADIUS = 0.16;

// Node styling across the dark→lit range: alpha lerps DIM→BRIGHT and radius
// grows by NODE_GROW at full highlight (shrinks back as the light leaves).
export const NODE_DIM_ALPHA = 0.18;
export const NODE_BRIGHT_ALPHA = 1;
export const NODE_GROW = 1.9;
// Reveal ignites nodes to this visible mid level; the roaming light then
// takes over (lifting to BRIGHT inside the highlight, settling to DIM).
export const NODE_REVEAL_ALPHA = 0.5;

// Edges fade with the light: dim static baseline + per-frame bright pass
// over edges inside the highlight.
export const EDGE_DIM_ALPHA = 0.1;
export const EDGE_BRIGHT_ALPHA = 0.55;

// Node glow is a radial-gradient sprite (bright core → transparent), drawn
// at radius × GLOW_SPRITE_SCALE. Pre-rendered once — never per-node
// createRadialGradient in the rAF loop.
export const GLOW_SPRITE_SCALE = 3.5;

// Depth-weighted glow: nearer features (smaller MediaPipe z) get brighter,
// larger nodes. Gain is precomputed per node from normalized depth.
export const DEPTH_GAIN_MIN = 0.7;
export const DEPTH_GAIN_MAX = 1.15;

export const WRAPPER_FADE_MS = 300; // framer-motion mount/unmount fade
